import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppConfigurationFeatures } from '../../../contexts/AppConfigurationContext';
import { useIdHotelOwner } from '../../../contexts/HotelOwnerContext';
import { useNotification } from '../../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../../utils/commonVariables/messages';
import { useServiceScraperPanel } from '../contexts/ServiceScraperPanelContext';
import { useZitoneoHotelPresentation } from '../contexts/ZitoneoContentContext';
import { scrapeServices } from '../services/serviceScraping';
import { saveServiceScraping } from '../services/serviceScrapingStorage';

/**
 * Runs a service scraping analysis, stores it in the history and opens the
 * review panel with the result. Must be used within ProviderServiceScraperPanel.
 * @returns The analyzing state and the analyze action.
 */
const useServiceScraping = () => {
    const ownerId = useIdHotelOwner();
    const location = useLocation();
    const { zitoneo: zitoneoConfigurationFeatures } =
        useAppConfigurationFeatures();
    const { setErrorMessage } = useNotification();
    const { hotel } = useZitoneoHotelPresentation();
    const { openPanelFromResponse } = useServiceScraperPanel();

    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const scopeKey = JSON.stringify([
        ownerId,
        hotel.idHotel,
        location.key,
        location.pathname,
        location.search,
        location.hash,
    ]);
    const scope = useMemo(() => ({ key: scopeKey }), [scopeKey]);
    const activeScopeRef = useRef(scope);
    activeScopeRef.current = scope;
    const mountedRef = useRef(false);
    const requestInProgress = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const analyze = async (scrapingUrl: string, categoryIds: string[]) => {
        if (requestInProgress.current) {
            return;
        }
        requestInProgress.current = true;
        const requestScope = scope;
        try {
            setIsAnalyzing(true);
            if (!ownerId || !hotel.idHotel) {
                throw new Error(
                    'Owner and hotel are required to analyze services.'
                );
            }
            const data = await scrapeServices(
                hotel.idHotel,
                scrapingUrl,
                categoryIds,
                zitoneoConfigurationFeatures.serviceScraperPromptKey
            );
            await saveServiceScraping(
                { ownerId, hotelId: hotel.idHotel.toString() },
                data
            );
            if (
                !mountedRef.current ||
                activeScopeRef.current !== requestScope
            ) {
                return;
            }
            if (!data.Result) {
                throw new Error(data.Message || 'Service scraping failed.');
            }
            openPanelFromResponse(data);
        } catch (error) {
            console.error('Error scraping services:', error);
            if (mountedRef.current && activeScopeRef.current === requestScope) {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } finally {
            requestInProgress.current = false;
            if (mountedRef.current) {
                setIsAnalyzing(false);
            }
        }
    };

    return { isAnalyzing, analyze };
};

export default useServiceScraping;
